// WHAT "CHANGED" MEANS (TGCLOUD-1902, rule 1): a field's value differs from the
// value last saved. Values, not touches: a value put back is no change, however
// many keystrokes it took.
//
// Both sides are normalised first, so that the form's own way of holding a value
// never reads as a change of it. Every rule here is one an edit screen in this
// codebase already needed:
//
//   text     trimmed; null, undefined, '' and whitespace are all "no value"
//   number   compared numerically when either side IS a number: an age typed as
//            '42' is the 42 on the record. Two strings stay strings, so '0012'
//            is not '12' (a hospital number is not a quantity)
//   flag     by truth when either side is a boolean, with 'true'/'false' strings
//            read as the flags they stand for (the phenotype and genotype radios
//            hand back strings over boolean records) and no value read as false
//   date     by local calendar day, through toLocalDateFromPickerOrImport, which
//            reads a picker's local instant, a stored 'YYYY-MM-DD' and an
//            importer's UTC midnight as the day each one means (CLAUDE.md
//            "Dates"). `dateGranularity: 'datetime'` compares to the minute,
//            for a field whose time is clinically meaningful
//   list     as a set by default (order and duplicates ignored), items keyed by
//            their id; `arrays: 'sequence'` where order means something. A single
//            value is the one-item list it stands for
//   object   by its id when it has one (`_id`, `id`, `userId`, `value`, `key`),
//            otherwise by its normalised content
//
// `kind` forces one rule where inferring it would be wrong ('text', 'number',
// 'flag', 'date', 'datetime', 'set', 'sequence'); `keyOf(item)` names an item's
// identity. Pure: no React, no clock.

import { isEqual } from 'lodash';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:$|[T ])/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ID_KEYS = ['_id', 'id', 'userId', 'value', 'key'];
const FLAG_TRUE = new Set(['true', '1', 'yes', 'y', 'on']);

/** No value: null, undefined, a blank string, an empty list or an object of no values. */
export const isBlankValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return Number.isNaN(value);
  if (value instanceof Date) return Number.isNaN(value.getTime());
  if (Array.isArray(value)) return value.every(isBlankValue);
  if (typeof value === 'object')
    return Object.values(value).every(isBlankValue);
  return false;
};

/** A value read as a flag: 'true', '1', 'yes' and true are set; anything else is not. */
export const toFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return !Number.isNaN(value) && value !== 0;
  if (typeof value === 'string')
    return FLAG_TRUE.has(value.trim().toLowerCase());
  return !isBlankValue(value);
};

/** Whether a value is a date: a Date, or a string that opens with an ISO date. */
export const isDateLike = (value) =>
  value instanceof Date ||
  (typeof value === 'string' && ISO_DATE.test(value.trim()));

const pad = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD', the local calendar day a value means, or null when it is no date. */
export const dayKeyOf = (value) => {
  const d = toLocalDateFromPickerOrImport(
    typeof value === 'string' ? value.trim() : value
  );
  if (!d || Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// The instant a value names, to the minute. A bare 'YYYY-MM-DD' is that day's
// local midnight (never `new Date('YYYY-MM-DD')`, which is UTC midnight).
const minuteKeyOf = (value) => {
  let time = NaN;
  if (value instanceof Date) time = value.getTime();
  else if (typeof value === 'string') {
    const s = value.trim();
    time = DATE_ONLY.test(s)
      ? toLocalDateFromPickerOrImport(s)?.getTime() ?? NaN
      : new Date(s).getTime();
  } else if (typeof value === 'number') time = value;
  return Number.isNaN(time) ? null : String(Math.floor(time / 60000));
};

const dateKeyOf = (value, granularity) =>
  granularity === 'datetime' ? minuteKeyOf(value) : dayKeyOf(value);

const toNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '')
    return Number(value.trim());
  return NaN;
};

const textOf = (value) =>
  typeof value === 'string' ? value.trim() : String(value);

// An object's content with its blanks dropped, strings trimmed, numbers as text
// and dates as days, keys sorted: what two objects are compared by when neither
// names an id.
const normaliseDeep = (value) => {
  if (isBlankValue(value)) return undefined;
  if (value instanceof Date || isDateLike(value))
    return dayKeyOf(value) ?? textOf(value);
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value))
    return value.map(normaliseDeep).filter((v) => v !== undefined);
  if (typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value).sort()) {
      const v = normaliseDeep(value[key]);
      if (v !== undefined) out[key] = v;
    }
    return out;
  }
  return value;
};

const idOf = (value) => {
  for (const key of ID_KEYS) {
    const v = value[key];
    if (v !== undefined && v !== null && v !== '' && typeof v !== 'object')
      return String(v);
  }
  return null;
};

/** An item's identity within a list: its id, its normalised value or its content. */
export const itemKeyOf = (item, keyOf) => {
  if (keyOf) {
    const key = keyOf(item);
    if (key !== undefined && key !== null) return String(key);
  }
  if (item instanceof Date) return dayKeyOf(item) ?? '';
  if (item && typeof item === 'object')
    return idOf(item) ?? JSON.stringify(normaliseDeep(item));
  if (isDateLike(item)) return dayKeyOf(item) ?? textOf(item);
  return textOf(item);
};

const asList = (value) =>
  (Array.isArray(value) ? value : [value]).filter((v) => !isBlankValue(v));

const sameList = (a, b, how, keyOf) => {
  const keysA = asList(a).map((v) => itemKeyOf(v, keyOf));
  const keysB = asList(b).map((v) => itemKeyOf(v, keyOf));
  if (how === 'sequence') return isEqual(keysA, keysB);
  const setA = [...new Set(keysA)].sort();
  const setB = [...new Set(keysB)].sort();
  return isEqual(setA, setB);
};

/**
 * Whether a field holds the same value in two states, after normalisation.
 *
 * @param {*} saved    the value as last saved
 * @param {*} current  the value in the form now
 * @param {object} [options]
 * @param {'auto'|'text'|'number'|'flag'|'date'|'datetime'|'set'|'sequence'} [options.kind]
 * @param {'day'|'datetime'} [options.dateGranularity]  'day' unless the time matters
 * @param {'set'|'sequence'} [options.arrays]  how a list compares, 'set' by default
 * @param {(item: *) => *} [options.keyOf]  an item's identity, for lists and objects
 * @returns {boolean} true when nothing changed
 */
export function compareFieldValues(saved, current, options = {}) {
  const {
    kind = 'auto',
    dateGranularity = 'day',
    arrays = 'set',
    keyOf
  } = options;
  const auto = kind === 'auto';

  if (
    kind === 'flag' ||
    (auto && (typeof saved === 'boolean' || typeof current === 'boolean'))
  )
    return toFlag(saved) === toFlag(current);

  const savedBlank = isBlankValue(saved);
  const currentBlank = isBlankValue(current);
  if (savedBlank || currentBlank) return savedBlank && currentBlank;

  if (
    kind === 'set' ||
    kind === 'sequence' ||
    (auto && (Array.isArray(saved) || Array.isArray(current)))
  )
    return sameList(saved, current, auto ? arrays : kind, keyOf);

  if (
    kind === 'date' ||
    kind === 'datetime' ||
    (auto && (isDateLike(saved) || isDateLike(current)))
  ) {
    const granularity =
      kind === 'datetime'
        ? 'datetime'
        : kind === 'date'
        ? 'day'
        : dateGranularity;
    const a = dateKeyOf(saved, granularity);
    const b = dateKeyOf(current, granularity);
    // Only two dates compare as dates. A date against something that is not one
    // ('Unknown') is compared as written, below.
    if (a !== null && b !== null) return a === b;
  }

  if (
    kind === 'number' ||
    (auto && (typeof saved === 'number' || typeof current === 'number'))
  ) {
    const a = toNumber(saved);
    const b = toNumber(current);
    if (!Number.isNaN(a) && !Number.isNaN(b)) return a === b;
  }

  if (
    kind !== 'text' &&
    ((saved && typeof saved === 'object') ||
      (current && typeof current === 'object'))
  )
    return itemKeyOf(saved, keyOf) === itemKeyOf(current, keyOf);

  return textOf(saved) === textOf(current);
}

export default compareFieldValues;
